import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Spinner,
  Alert,
  Badge,
  Label,
  Input,
} from 'reactstrap';
import axiosInstance from '../../api/Axios';

const ProviderManageProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/api/properties/${id}/`);
      setProperty(response.data);
    } catch (err) {
      console.error('Error fetching property:', err);
      setError(err.response?.data?.message || 'Failed to load property details');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestInspection = async () => {
    try {
      setUpdating(true);
      setError(null);
      setSuccessMessage(null);

      await axiosInstance.patch(`/api/properties/${id}/`, {
        inspection_status: 'inspection_requested'
      });

      setSuccessMessage('Inspection request submitted successfully! An inspector will be assigned soon.');
      
      // Refresh property data
      await fetchProperty();
    } catch (err) {
      console.error('Error requesting inspection:', err);
      setError(err.response?.data?.message || 'Failed to request inspection');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      setUpdating(true);
      setError(null);
      setSuccessMessage(null);

      const newStatus = property.status === 'active' ? 'inactive' : 'active';

      await axiosInstance.patch(`/api/properties/${id}/`, {
        status: newStatus
      });

      setSuccessMessage(`Property ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
      
      // Refresh property data
      await fetchProperty();
    } catch (err) {
      console.error('Error updating property status:', err);
      setError(err.response?.data?.message || 'Failed to update property status');
    } finally {
      setUpdating(false);
    }
  };

  const getInspectionStatusBadge = (status) => {
    const statusMap = {
      'inspection_pending': { color: 'secondary', text: 'Inspection Pending' },
      'inspection_requested': { color: 'info', text: 'Inspection Requested' },
      'inspection_booked': { color: 'primary', text: 'Inspection Booked' },
      'inspection_passed': { color: 'success', text: 'Inspection Passed' },
      'inspection_failed': { color: 'danger', text: 'Inspection Failed' },
    };

    const config = statusMap[status] || { color: 'secondary', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const getPropertyStatusBadge = (status) => {
    const statusMap = {
      'created': { color: 'secondary', text: 'Created' },
      'active': { color: 'success', text: 'Active' },
      'inactive': { color: 'warning', text: 'Inactive' },
      'suspended': { color: 'danger', text: 'Suspended' },
    };

    const config = statusMap[status] || { color: 'secondary', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading property details...</p>
        </div>
      </Container>
    );
  }

  if (error && !property) {
    return (
      <Container className="mt-4">
        <Alert color="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
        <Button color="secondary" onClick={() => navigate('/provider-properties')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to Properties
        </Button>
      </Container>
    );
  }

  const inspectionPassed = property?.inspection_status === 'inspection_passed';
  const canActivate = inspectionPassed;

  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-sliders me-2"></i>
                Manage Property
              </h2>
              <p className="text-muted mb-0">{property?.trading_name}</p>
            </div>
            <Button 
              color="secondary" 
              outline 
              onClick={() => navigate('/provider-properties')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Properties
            </Button>
          </div>
        </Col>
      </Row>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert color="success" toggle={() => setSuccessMessage(null)}>
          <i className="bi bi-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Property Information Card */}
      <Row className="mb-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Property Information
              </CardTitle>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <strong>Trading Name:</strong>
                    <p className="mb-0">{property?.trading_name}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Address:</strong>
                    <p className="mb-0">
                      {[
                        property?.addresslines,
                        property?.suburb,
                        property?.city,
                        property?.province,
                        property?.postcode
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  <div className="mb-3">
                    <strong>Contact:</strong>
                    <p className="mb-0">
                      <i className="bi bi-envelope me-2"></i>{property?.email}<br />
                      <i className="bi bi-telephone me-2"></i>{property?.phone}
                    </p>
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <strong>Property Status:</strong>
                    <div>{getPropertyStatusBadge(property?.status)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Inspection Status:</strong>
                    <div>{getInspectionStatusBadge(property?.inspection_status)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Capacity:</strong>
                    <p className="mb-0">
                      {property?.number_of_beds || 0} Beds | {property?.number_of_rooms || 0} Rooms | {property?.no_of_blocks || 0} Blocks
                    </p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Inspection Status Section */}
      {inspectionPassed ? (
        /* Inspection Passed - Show Activation Toggle */
        <Row>
          <Col md="12">
            <Card className="border-success">
              <CardBody>
                <CardTitle tag="h5" className="text-success mb-4">
                  <i className="bi bi-check-circle me-2"></i>
                  Property Inspection Passed
                </CardTitle>
                <p className="text-muted">
                  Your property has successfully passed inspection. You can now activate or deactivate the property to control its availability.
                </p>

                {property?.inspection_date && (
                  <div className="mb-3">
                    <small className="text-muted">
                      <i className="bi bi-calendar-check me-2"></i>
                      Inspected on: {new Date(property.inspection_date).toLocaleDateString()}
                    </small>
                  </div>
                )}

                {property?.inspection_notes && (
                  <div className="mb-4 p-3 bg-light rounded">
                    <strong>Inspector's Notes:</strong>
                    <p className="mb-0 mt-2">{property.inspection_notes}</p>
                  </div>
                )}

                <div className="mb-3">
                  <div className="d-flex align-items-center">
                    <div className="form-check form-switch" style={{ fontSize: '1.5rem' }}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="property-status-switch"
                        checked={property?.status === 'active'}
                        onChange={handleToggleStatus}
                        disabled={updating}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                    <Label for="property-status-switch" className="ms-3 mb-0" style={{ cursor: 'pointer' }}>
                      <strong>
                        {property?.status === 'active' ? (
                          <span className="text-success">
                            <i className="bi bi-toggle-on me-2"></i>
                            Property is Active
                          </span>
                        ) : (
                          <span className="text-warning">
                            <i className="bi bi-toggle-off me-2"></i>
                            Property is Inactive
                          </span>
                        )}
                      </strong>
                      <br />
                      <small className="text-muted">
                        {property?.status === 'active'
                          ? 'Property is visible and available for bookings'
                          : 'Property is hidden and not available for bookings'}
                      </small>
                    </Label>
                  </div>
                </div>

                <div className="alert alert-info mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <small>
                    Toggle this switch to make your property {property?.status === 'active' ? 'inactive' : 'active'}.
                    {property?.status === 'active'
                      ? ' Deactivating will hide it from students.'
                      : ' Activating will make it visible to students for booking.'}
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        /* Inspection Not Passed - Show Request Inspection */
        <Row>
          <Col md="12">
            <Card className="border-warning">
              <CardBody>
                <CardTitle tag="h5" className="text-warning mb-4">
                  <i className="bi bi-clipboard-check me-2"></i>
                  Inspection Required
                </CardTitle>
                
                {property?.inspection_status === 'inspection_pending' && (
                  <>
                    <p className="text-muted">
                      Your property needs to be inspected before it can be activated. Please request an inspection to proceed.
                    </p>
                    <Button 
                      color="warning" 
                      size="lg"
                      onClick={handleRequestInspection}
                      disabled={updating}
                    >
                      {updating ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Requesting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Request Inspection
                        </>
                      )}
                    </Button>
                  </>
                )}

                {property?.inspection_status === 'inspection_requested' && (
                  <>
                    <Alert color="info">
                      <i className="bi bi-clock-history me-2"></i>
                      <strong>Inspection Requested</strong>
                      <p className="mb-0 mt-2">
                        Your inspection request has been submitted. An inspector will be assigned shortly.
                      </p>
                    </Alert>
                  </>
                )}

                {property?.inspection_status === 'inspection_booked' && (
                  <>
                    <Alert color="primary">
                      <i className="bi bi-calendar-check me-2"></i>
                      <strong>Inspection Scheduled</strong>
                      <p className="mb-0 mt-2">
                        Your property inspection has been scheduled. The inspector will visit soon.
                      </p>
                    </Alert>
                  </>
                )}

                {property?.inspection_status === 'inspection_failed' && (
                  <>
                    <Alert color="danger">
                      <i className="bi bi-x-circle me-2"></i>
                      <strong>Inspection Failed</strong>
                      <p className="mb-0 mt-2">
                        Your property did not pass the inspection. Please review the inspector's notes below and address the issues.
                      </p>
                    </Alert>

                    {property?.inspection_notes && (
                      <div className="mb-4 p-3 bg-light rounded">
                        <strong>Inspector's Notes:</strong>
                        <p className="mb-0 mt-2">{property.inspection_notes}</p>
                      </div>
                    )}

                    <Button 
                      color="warning" 
                      onClick={handleRequestInspection}
                      disabled={updating}
                    >
                      {updating ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Requesting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Request Re-Inspection
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProviderManageProperty;
